import { Button, Text } from '@southfields-digital/mpxlive-components';
import { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { Tooltip } from 'src/components/Tooltip';
import { StateType } from 'src/redux/reducers';
import {
  REQUEST_DELETE_PLAYLIST,
  REQUEST_DUPLICATE_PLAYLIST,
} from 'src/redux/reducers/playlist';
import { useMosActions } from 'src/components/Playlists/useMosActions';

import type { PlaylistActionBarProps } from './PlaylistActionBar';

export default function MosActionBar({ playlistId, editable, onEdit, onAddGraphic }: PlaylistActionBarProps) {
  const dispatch = useDispatch();
  const { mosId, connected, autoFetch, statusLoading, mosPlaylist, handleToggleAutoFetch, handlePullMos } = useMosActions(playlistId);

  const rundownId = useSelector((state: StateType) => state.rundown.detail.data?.id);

  const handleDuplicate = useCallback(() => {
    if (!playlistId || !rundownId) return;
    dispatch({
      type: REQUEST_DUPLICATE_PLAYLIST,
      payload: { id: playlistId, rundownId },
    });
  }, [dispatch, playlistId, rundownId]);

  const handleDelete = useCallback(() => {
    if (!playlistId) return;
    dispatch({
      type: REQUEST_DELETE_PLAYLIST,
      payload: { id: playlistId },
    });
  }, [dispatch, playlistId]);

  const disabled = !mosId;

  return (
    <div className="flex gap-1.5 w-full h-fit py-1.5 px-3 items-center">
      <div className="flex items-center gap-1.5">
        <span className={`inline-block w-2 h-2 rounded-full ${connected ? 'bg-emerald-400' : 'bg-red-400'}`} />
        <Text className="text-nowrap text-xs font-semibold">MOS</Text>
      </div>

      <div className="flex gap-1 ml-auto">
        <Tooltip overlay={autoFetch ? 'Disconnect' : 'Connect'}>
          <Button
            iconProps={{ icon: autoFetch ? 'link-horizontal-break' : 'link-horizontal', ...(connected && autoFetch && { color: '#4155ff' }) }}
            variant="muted"
            buttonSize="small"
            disabled={disabled || statusLoading || !connected}
            onClick={handleToggleAutoFetch}
          />
        </Tooltip>
        <Tooltip overlay="Pull MOS">
          <Button
            iconProps={{ icon: 'file-arrow-down' }}
            variant="muted"
            buttonSize="small"
            disabled={disabled || statusLoading || !mosPlaylist || !connected}
            onClick={handlePullMos}
          />
        </Tooltip>

        <div className="w-px h-5 bg-[rgba(249,250,251,0.1)] self-center mx-0.5" />
        <Tooltip overlay="Add graphic">
          <Button
            iconProps={{ icon: 'plus' }}
            variant="muted"
            buttonSize="small"
            disabled={!editable}
            onClick={onAddGraphic}
          />
        </Tooltip>
        <Tooltip overlay="Rename playlist">
          <Button
            iconProps={{ icon: 'pencil' }}
            variant="muted"
            buttonSize="small"
            disabled={!editable || !playlistId}
            onClick={onEdit}
          />
        </Tooltip>
        <Tooltip overlay="Duplicate playlist">
          <Button
            iconProps={{ icon: 'copy' }}
            variant="muted"
            buttonSize="small"
            disabled={!editable || !playlistId}
            onClick={handleDuplicate}
          />
        </Tooltip>
        <Tooltip overlay="Delete playlist">
          <Button
            iconProps={{ icon: 'trash' }}
            variant="muted"
            buttonSize="small"
            disabled={!editable || !playlistId}
            onClick={handleDelete}
          />
        </Tooltip>
      </div>
    </div>
  );
}
